// ─────────────────────────────────────────────────────────────────────────────
// FIT SYSTEM v2 — MARK ALTITUDE
// Modular size recommendation system with continuous linear algorithm.
// Architecture: 6 decoupled classes + 2 orchestrators.
// ─────────────────────────────────────────────────────────────────────────────

function getFitStoqSDKWhenReady(requiredMethod = 'openInlineForm', timeout = 15000) {
  return new Promise((resolve) => {
    const isReady = () => (
      window._RestockRocket &&
      typeof window._RestockRocket[requiredMethod] === 'function'
    );

    if (isReady()) {
      resolve(window._RestockRocket);
      return;
    }

    let finished = false;
    let interval = null;

    const cleanup = () => {
      window.removeEventListener('stoq:loaded', onLoaded);
      if (interval) clearInterval(interval);
    };

    const finish = () => {
      if (finished) return;
      finished = true;
      cleanup();
      resolve(window._RestockRocket);
    };

    const onLoaded = () => {
      if (isReady()) finish();
    };

    window.addEventListener('stoq:loaded', onLoaded);

    interval = setInterval(() => {
      if (isReady()) finish();
    }, 150);

    setTimeout(() => {
      if (finished) return;
      finished = true;
      cleanup();
      console.error('[MA FIT STOQ SDK TIMEOUT]', requiredMethod);
      resolve(null);
    }, timeout);
  });
}
window.getFitStoqSDKWhenReady = getFitStoqSDKWhenReady;

async function mountFitStoqInlineForm(productId, productName, size) {
  const container = document.getElementById('ma-fit-stoq');
  if (!container) return;

  const stoqSDK = await getFitStoqSDKWhenReady('openInlineForm');
  if (!stoqSDK) return;

  if (typeof window.resolveVariantIdForStoq === 'function') {
    const info = await window.resolveVariantIdForStoq(productId, productName, size);
    if (!info || !info.productId || !info.variantId) {
      console.error('[MA STOQ] Product/variant resolution failed');
      return;
    }

    const currentContainer = document.getElementById('ma-fit-stoq');
    if (!currentContainer) return;

    try {
      const productData = {
        id: Number(info.productId),
        variants: [
          {
            id: Number(info.variantId),
            available: false
          }
        ]
      };

      stoqSDK.openInlineForm(
        productData,
        String(info.variantId),
        null,
        '#ma-fit-stoq',
        'beforeend'
      );
    } catch (e) {
      console.error('[MA FIT STOQ INLINE ERROR]', e);
    }
  }
}
window.mountFitStoqInlineForm = mountFitStoqInlineForm;

// ═══════════════════════════════════════════════════════════════════════════════
// 1. FitProfileStorage — sessionStorage persistence (session-scoped)
// ═══════════════════════════════════════════════════════════════════════════════
class FitProfileStorage {
  constructor(key = 'mark-altitude-fit-profile-v3.1') {
    this._key = key;
  }

  get() {
    try {
      const raw = localStorage.getItem(this._key);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  }

  save(height, weight) {
    let cleanHeight = String(height).trim().replace(/[^\d.,]/g, '').replace(',', '.');
    let h = parseFloat(cleanHeight);
    if (!isNaN(h) && h > 0 && h <= 2.3) {
      h = Math.round(h * 100);
    }
    let cleanWeight = String(weight).trim().replace(/[^\d.,]/g, '').replace(',', '.');
    let w = parseFloat(cleanWeight);

    const profile = {
      heightCm: isNaN(h) ? 0 : h,
      weightKg: isNaN(w) ? 0 : w,
      updatedAt: Date.now()
    };
    try { localStorage.setItem(this._key, JSON.stringify(profile)); } catch {}
    return profile;
  }

  exists() {
    const p = this.get();
    return p !== null && p.heightCm > 0 && p.weightKg > 0;
  }

  clear() {
    try { localStorage.removeItem(this._key); } catch {}
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// 2. FitProfileRepository — Repository Pattern (public API)
// ═══════════════════════════════════════════════════════════════════════════════
class FitProfileRepository {
  constructor(storage = new FitProfileStorage()) {
    this._storage = storage;
  }

  getProfile() {
    const raw = this._storage.get();
    if (!raw) return null;
    return {
      height: raw.heightCm || 0,
      weight: raw.weightKg || 0,
      heightCm: raw.heightCm || 0,
      weightKg: raw.weightKg || 0,
      updatedAt: raw.updatedAt || 0
    };
  }

  saveProfile(height, weight) {
    const res = this._storage.save(height, weight);
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('ma:fit-updated', { detail: { action: 'save', height, weight } }));
    }
    return res;
  }

  saveManualSelection() {}

  exists() {
    return this._storage.exists();
  }

  clear() {
    this._storage.clear();
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('ma:fit-updated', { detail: { action: 'clear' } }));
    }
  }

  clearProfile() {
    this.clear();
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// 3. FitRecommendationEngine — mark-fit-v3.1-provisional (explicit matrix)
// ═══════════════════════════════════════════════════════════════════════════════

const FIT_ALGORITHM_VERSION = 'mark-fit-v3.1-provisional';

const SIZES = Object.freeze(['S', 'M', 'L', 'XL', 'XXL']);

const sizeFinderProductConfig = Object.freeze({
  default: Object.freeze({ sizeOffset: 0 }),
  t01:     Object.freeze({ sizeOffset: 0 }),
  j01:     Object.freeze({ sizeOffset: 0 })
});

function getProductType(productId) {
  if (productId === 'MA-TE-BK' || productId === 'MA-TE-WH') return 't01';
  if (productId === 'MA-DJ-01') return 'j01';
  return null;
}

// Rows: height bands   → 150-164, 165-174, 175-184, 185-194, 195-205
// Cols: weight bands    → 45-59,   60-66,   67-79,   80-89,   90-99,   100-120
const FIT_SIZE_MATRIX = Object.freeze([
  Object.freeze(['S', 'S', 'M', 'L', 'XL', 'XXL']),  // 150–164 cm
  Object.freeze(['S', 'S', 'M', 'L', 'XL', 'XXL']),  // 165–174 cm
  Object.freeze(['S', 'M', 'M', 'L', 'XL', 'XXL']),  // 175–184 cm
  Object.freeze(['M', 'M', 'M', 'L', 'XL', 'XXL']),  // 185–194 cm
  Object.freeze(['M', 'M', 'L', 'L', 'XL', 'XXL']),  // 195–205 cm
]);

class FitRecommendationEngine {
  recommend(height, weight, productType = 't01') {
    const h = this._parseHeight(height);
    const w = this._parseWeight(weight);
    const validation = this.validate(h, w);

    if (!validation.valid) {
      return {
        size: null,
        valid: false,
        error: validation.error,
        field: validation.field
      };
    }

    const rowIndex = this._getHeightRow(h);
    const colIndex = this._getWeightCol(w);
    const baseSize = FIT_SIZE_MATRIX[rowIndex][colIndex];

    // Apply discrete product offset
    const normalizedProductType = String(productType || 'default').trim().toLowerCase();
    const config = sizeFinderProductConfig[normalizedProductType]
                ?? sizeFinderProductConfig.default;
    let size = baseSize;
    if (config.sizeOffset !== 0) {
      const sizeIdx = SIZES.indexOf(baseSize);
      const shifted = Math.max(0, Math.min(SIZES.length - 1, sizeIdx + config.sizeOffset));
      size = SIZES[shifted];
    }

    return {
      valid: true,
      size,
      baseSize,
      sizeOffset: config.sizeOffset,
      algorithmVersion: FIT_ALGORITHM_VERSION
    };
  }

  recommendSize(height, weight, productType = 't01') {
    const result = this.recommend(height, weight, productType);
    return result.valid ? result.size : null;
  }

  validate(height, weight) {
    const h = (typeof height === 'number') ? height : this._parseHeight(height);
    const w = (typeof weight === 'number') ? weight : this._parseWeight(weight);

    if (!Number.isFinite(h)) {
      return { valid: false, field: 'height', error: 'INVALID_HEIGHT' };
    }
    if (h < 150 || h > 205) {
      return { valid: false, field: 'height', error: 'HEIGHT_OUT_OF_RANGE' };
    }
    if (!Number.isFinite(w)) {
      return { valid: false, field: 'weight', error: 'INVALID_WEIGHT' };
    }
    if (w < 45 || w > 120) {
      return { valid: false, field: 'weight', error: 'WEIGHT_OUT_OF_RANGE' };
    }
    return { valid: true };
  }

  _getHeightRow(h) {
    if (h < 165) return 0;
    if (h < 175) return 1;
    if (h < 185) return 2;
    if (h < 195) return 3;
    return 4;
  }

  _getWeightCol(w) {
    if (w < 60)  return 0;
    if (w < 67)  return 1;
    if (w < 80)  return 2;
    if (w < 90)  return 3;
    if (w < 100) return 4;
    return 5;
  }

  _parseHeight(value) {
    if (value === null || value === undefined || value === '') return NaN;

    const normalized = String(value)
      .trim()
      .toLowerCase()
      .replace(',', '.')
      .replace('cm', '')
      .replace('m', '');

    let height = Number.parseFloat(normalized);

    if (!Number.isFinite(height)) return NaN;
    if (height >= 1.5 && height <= 2.05) height *= 100;

    return Math.round(height);
  }

  _parseWeight(value) {
    if (value === null || value === undefined || value === '') return NaN;

    const normalized = String(value)
      .trim()
      .toLowerCase()
      .replace(',', '.')
      .replace('kg', '')
      .replace('lbs', '');

    return Number.parseFloat(normalized);
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// 4. ShopifyVariantResolver — dynamic inventory
// ═══════════════════════════════════════════════════════════════════════════════
class ShopifyVariantResolver {
  constructor(productData) {
    this._product = productData;
    this._allSizes = productData.sizes || [];
    this._oosCache = productData.outOfStockSizes || [];
  }

  isAvailable(size) {
    return !this._oosCache.includes(size);
  }

  getAllSizes() {
    return [...this._allSizes];
  }

  async refreshFromShopify(handle) {
    if (!handle) return;
    const isShopify = window.Shopify && window.Shopify.shop;
    if (!isShopify) return;

    try {
      const res = await fetch(`/products/${handle}.js`);
      if (!res.ok) return;
      const data = await res.json();
      if (data && data.variants) {
        this._oosCache = data.variants
          .filter(v => !v.available)
          .map(v => {
            const sizeOpt = v.option1 || v.option2 || v.option3 || '';
            return sizeOpt.toUpperCase();
          });
      }
    } catch {}
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// 5. FitDrawerUI — side drawer with Massimo Dutti visual design
// ═══════════════════════════════════════════════════════════════════════════════
function getAssetUrl(src) {
  if (!src) return '';
  if (src.startsWith('http://') || src.startsWith('https://') || src.startsWith('data:')) return src;
  const filename = src.split('/').pop();
  if (window.MA_ASSETS && window.MA_ASSETS[filename]) {
    return window.MA_ASSETS[filename];
  }
  if (window.ShopifyAssetUrl) {
    return window.ShopifyAssetUrl + filename;
  }
  if (window.MA_ASSET_URLS && window.MA_ASSET_URLS[filename]) {
    return window.MA_ASSET_URLS[filename];
  }
  if (!src.includes('/')) {
    return `images/${filename}`;
  }
  return src;
}

function getProductImageUrl(productId, productData) {
  if (productId === 'MA-TE-BK') return getAssetUrl('t01-polo-negro-miniatura-home.jpg');
  if (productId === 'MA-TE-WH') return getAssetUrl('t01-polo-blanco-miniatura-home.jpg');
  if (productId === 'MA-DF-01') return getAssetUrl('c01-bolso-viaje-cuero-miniatura-home.jpg');
  if (productId === 'MA-DJ-01') return getAssetUrl('j01-casaca-plumas-miniatura-home.jpg');
  
  if (productData && productData.images && productData.images.length > 0) {
    return getAssetUrl(productData.images[0]);
  }
  return '';
}

// ─────────────────────────────────────────────────────────────
// MARK ALTITUDE × STOQ — FROZEN INTEGRATION
//
// PDP:
// STOQ native openInlineForm() + STOQ Dev Mode CSS.
//
// Home / Collection:
// Custom MARK ALTITUDE form + STOQ public intents API.
// Home visual styles are intentionally injected at runtime by
// ensureHomeStoqStyles() to isolate them from theme CSS cascade.
//
// Do not consolidate these flows, move Home STOQ styles back to
// global CSS, reintroduce openModal(), or change success logic
// without an explicit STOQ integration task.
// ─────────────────────────────────────────────────────────────

function ensureHomeStoqStyles() {
  if (typeof document === 'undefined' || !document.head) return;
  if (document.getElementById('ma-home-stoq-runtime-styles')) return;

  const style = document.createElement('style');
  style.id = 'ma-home-stoq-runtime-styles';
  style.textContent = `
    [data-fit-drawer] #ma-home-fit-stoq-form .ma-stoq-input {
      display: block !important;
      width: 100% !important;
      height: 48px !important;
      margin: 0 0 8px 0 !important;
      padding: 0 16px !important;
      background: #ffffff !important;
      color: #0a0a0a !important;
      border: 1px solid #0a0a0a !important;
      border-radius: 0 !important;
      box-sizing: border-box !important;
      box-shadow: none !important;
      outline: none !important;
      font-family: "Inter Tight", -apple-system, BlinkMacSystemFont, sans-serif !important;
      font-size: 10.5px !important;
      font-weight: 300 !important;
      line-height: 1 !important;
      letter-spacing: 0.04em !important;
      text-transform: uppercase !important;
    }

    [data-fit-drawer] #ma-home-fit-stoq-form .ma-stoq-input::placeholder {
      color: #737373 !important;
      opacity: 1 !important;
      font-family: "Inter Tight", -apple-system, BlinkMacSystemFont, sans-serif !important;
      font-size: 11px !important;
      font-weight: 300 !important;
      letter-spacing: 0.06em !important;
      text-transform: uppercase !important;
    }

    [data-fit-drawer] #ma-home-fit-stoq-form .ma-stoq-submit {
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      width: 100% !important;
      height: 46px !important;
      margin: 8px 0 0 !important;
      padding: 0 16px !important;
      background-color: #0a0a0a !important;
      color: #ffffff !important;
      border: 1px solid #0a0a0a !important;
      border-radius: 0 !important;
      box-shadow: none !important;
      outline: none !important;
      font-family: "Inter Tight", sans-serif !important;
      font-size: 12px !important;
      font-weight: 500 !important;
      line-height: 1 !important;
      letter-spacing: 0.15em !important;
      text-transform: uppercase !important;
      cursor: pointer !important;
      opacity: 1 !important;
      transform: translateY(0) !important;
      transition:
        background-color 200ms cubic-bezier(0.16, 1, 0.3, 1),
        opacity 200ms cubic-bezier(0.16, 1, 0.3, 1),
        transform 200ms cubic-bezier(0.16, 1, 0.3, 1) !important;
    }

    @media (hover: hover) {
      [data-fit-drawer] #ma-home-fit-stoq-form .ma-stoq-submit:not(:disabled):hover {
        background-color: #171717 !important;
        opacity: 0.90 !important;
        transform: translateY(-1px) !important;
      }
    }

    [data-fit-drawer] #ma-home-fit-stoq-form .ma-stoq-submit:not(:disabled):active {
      background-color: #0a0a0a !important;
      opacity: 0.78 !important;
      transform: translateY(0) !important;
    }

    [data-fit-drawer] #ma-home-fit-stoq-form .ma-stoq-submit:disabled {
      opacity: 0.55 !important;
      cursor: wait !important;
      transform: translateY(0) !important;
    }

    [data-fit-drawer] .ma-stoq-success-outer {
      display: none;
      width: 100% !important;
      box-sizing: border-box !important;
      margin: 0 !important;
    }

    [data-fit-drawer] .ma-stoq-success-inner {
      width: 100% !important;
      min-height: 72px !important;
      box-sizing: border-box !important;
      padding: 14px 16px !important;
      border: 1px solid var(--color-gray-200, #e5e5e5) !important;
      border-radius: 0 !important;
      background-color: #ffffff !important;
      box-shadow: none !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      text-align: center !important;
    }

    [data-fit-drawer] .ma-stoq-success-text {
      display: block !important;
      margin: 0 !important;
      padding: 0 !important;
      font-family: "Inter Tight", -apple-system, BlinkMacSystemFont, sans-serif !important;
      font-size: 11px !important;
      font-weight: 500 !important;
      line-height: 1.4 !important;
      letter-spacing: 0.10em !important;
      text-align: center !important;
      text-transform: uppercase !important;
      color: #0a0a0a !important;
    }

    @media (prefers-reduced-motion: reduce) {
      [data-fit-drawer] #ma-home-fit-stoq-form .ma-stoq-input,
      [data-fit-drawer] #ma-home-fit-stoq-form .ma-stoq-submit {
        transition: none !important;
      }
    }
  `;
  document.head.appendChild(style);
}

function renderMarkAltitudeStoqShell(isProductPage, isEn) {
  if (!isProductPage) {
    ensureHomeStoqStyles();
  }
  return `
    <p style="font-size: 12.5px; font-weight: 300; text-align: center; color: var(--color-black); line-height: 1.45; margin: 0 0 8px;">
      ${isEn ? 'This size is temporarily out of stock. Leave us your email and we will notify you when it is available again.' : 'Esta talla está temporalmente agotada. Déjanos tu correo y te avisaremos cuando vuelva a estar disponible.'}
    </p>
    <div class="ma-stoq-shell" style="width: 100%; margin: 0 0 10px;">
      <div id="ma-stoq-form-container" class="ma-stoq-form-container" style="width: 100%;">
        ${isProductPage ? `
          <div id="ma-fit-stoq" style="width: 100%; margin: 0;"></div>
        ` : `
          <form id="ma-home-fit-stoq-form" class="ma-stoq-form" style="width: 100%; margin: 0;">
            <input type="email" class="ma-stoq-input" placeholder="${isEn ? 'EMAIL ADDRESS' : 'CORREO ELECTRÓNICO'}" required value="" autocomplete="off" />
            <button type="submit" class="ma-stoq-submit">
              ${isEn ? 'NOTIFY ME' : 'AVÍSAME'}
            </button>
          </form>
          <p
            id="ma-home-stoq-error-msg"
            role="alert"
            style="
              display: none;
              margin: 8px 0 0;
              padding: 0;
              color: #a52525;
              font-family: 'Inter Tight', sans-serif;
              font-size: 10px;
              font-weight: 400;
              line-height: 1.35;
              letter-spacing: 0.05em;
              text-align: center;
              text-transform: uppercase;
            "
          >
            ${isEn
              ? 'COULD NOT REGISTER. PLEASE TRY AGAIN.'
              : 'NO SE PUDO REGISTRAR. INTÉNTALO DE NUEVO.'
            }
          </p>
        `}
      </div>
      <div
        id="ma-fit-stoq-success-msg"
        class="ma-stoq-success-outer"
        hidden
      >
        <div class="ma-stoq-success-inner">
          <span class="ma-stoq-success-text">
            ${isEn ? 'DONE. YOU WILL BE NOTIFIED WHEN RESTOCKED.' : 'LISTO. TE NOTIFICAREMOS CUANDO HAYA STOCK.'}
          </span>
        </div>
      </div>
    </div>
  `;
}

class FitDrawerUI {
  constructor() {
    this._drawer = null;
    this._overlay = null;
    this._isOpen = false;
    this._triggerElement = null;
    this._productData = null;
    this._productType = 't01';
    this._onApplyCallback = null;
    this._onSubmitCallback = null;
    this._onRecalcCallback = null;
    this._onClearCallback = null;
    this._onCloseCallback = null;
    this._boundKeydown = this._handleKeydown.bind(this);
    this._focusTrapBound = this._focusTrap.bind(this);

    // Active units
    this._heightUnit = 'cm';
    this._weightUnit = 'kg';
  }

  render(lang) {
    const existing = document.querySelector('[data-fit-drawer]');
    if (existing) existing.remove();
    const existingOverlay = document.querySelector('[data-fit-overlay]');
    if (existingOverlay) existingOverlay.remove();

    const isEn = lang === 'en';
    this._lang = lang;

    // Overlay
    this._overlay = document.createElement('div');
    this._overlay.className = 'fit-drawer__overlay';
    this._overlay.setAttribute('data-fit-overlay', '');
    this._overlay.setAttribute('aria-hidden', 'true');
    document.body.appendChild(this._overlay);

    // Drawer
    this._drawer = document.createElement('aside');
    this._drawer.className = 'fit-drawer';
    this._drawer.setAttribute('data-fit-drawer', '');
    this._drawer.setAttribute('aria-hidden', 'true');
    this._drawer.setAttribute('role', 'dialog');
    this._drawer.setAttribute('aria-modal', 'true');
    this._drawer.setAttribute('aria-labelledby', 'fit-drawer-title');

    const tableHtml = `
      <div class="fit-accordion-table-wrapper">
        <table class="fit-accordion-table">
          <thead>
            <tr>
              <th style="text-align: left;">${isEn ? 'SIZE' : 'TALLA'}</th>
              <th>${isEn ? 'CHEST' : 'PECHO'}</th>
              <th>${isEn ? 'WAIST' : 'CINTURA'}</th>
              <th>${isEn ? 'HEIGHT' : 'ESTATURA'}</th>
            </tr>
          </thead>
          <tbody>
            <tr><td style="text-align: left; font-weight: 500;">S</td><td>92–96</td><td>78–82</td><td>165–170</td></tr>
            <tr><td style="text-align: left; font-weight: 500;">M</td><td>96–100</td><td>82–86</td><td>170–175</td></tr>
            <tr><td style="text-align: left; font-weight: 500;">L</td><td>100–105</td><td>86–91</td><td>175–180</td></tr>
            <tr><td style="text-align: left; font-weight: 500;">XL</td><td>105–110</td><td>91–96</td><td>180–185</td></tr>
            <tr><td style="text-align: left; font-weight: 500;">XXL</td><td>110–115</td><td>96–102</td><td>180–185</td></tr>
          </tbody>
        </table>
      </div>
      <p class="fit-accordion-disclaimer">${isEn
        ? 'Measurements are a body reference. Recommendation may vary slightly depending on each person\'s proportions.'
        : 'Las medidas son una referencia corporal. La recomendación puede variar ligeramente según las proporciones de cada persona.'
      }</p>
    `;

    this._drawer.innerHTML = `
      <header class="fit-drawer__header">
        <button class="fit-drawer__close" data-fit-close aria-label="${isEn ? 'Close' : 'Cerrar'}">✕</button>
      </header>
      <div class="fit-drawer__body">
        <div class="fit-drawer__form-view" data-fit-form-view>
          <h2 class="fit-drawer__title" id="fit-drawer-title" style="margin-bottom: 20px;">${isEn ? 'BASIC INFORMATION' : 'INFORMACIÓN BÁSICA'}</h2>
          <p class="fit-drawer__intro">${isEn
            ? 'Once you fill in your details, we will indicate your recommended size in MARK ALTITUDE.'
            : 'Una vez que rellenes tus datos, te indicaremos tu talla recomendada en MARK ALTITUDE.'
          }</p>

          <div class="fit-drawer__form" data-fit-form>
            <div class="fit-drawer__field">
              <label class="fit-drawer__label" for="fit-height">${isEn ? 'INDICATE YOUR HEIGHT *' : 'INDICA TU ALTURA *'}</label>
              <div class="fit-measurement-control">
                <div class="fit-measurement-input-wrapper">
                  <select id="fit-height" class="fit-measurement-select" data-fit-height></select>
                  <span class="fit-select-chevron">˅</span>
                </div>
                <div class="fit-unit-toggle" data-fit-height-toggle>
                  <button type="button" class="fit-unit-btn is-active" data-unit="cm">CM</button>
                  <button type="button" class="fit-unit-btn" data-unit="m">M</button>
                </div>
              </div>
              <p class="fit-drawer__field-error" data-fit-height-error></p>
            </div>

            <div class="fit-drawer__field">
              <label class="fit-drawer__label" for="fit-weight">${isEn ? 'INDICATE YOUR WEIGHT *' : 'INDICA TU PESO *'}</label>
              <div class="fit-measurement-control">
                <div class="fit-measurement-input-wrapper">
                  <select id="fit-weight" class="fit-measurement-select" data-fit-weight></select>
                  <span class="fit-select-chevron">˅</span>
                </div>
                <div class="fit-unit-toggle" data-fit-weight-toggle>
                  <button type="button" class="fit-unit-btn is-active" data-unit="kg">KG</button>
                  <button type="button" class="fit-unit-btn" data-unit="lbs">LBS</button>
                </div>
              </div>
              <p class="fit-drawer__field-error" data-fit-weight-error></p>
            </div>

            <p class="fit-drawer__disclaimer">${isEn
              ? 'We will use your height and weight solely to calculate the size that best fits you. This data is not linked to your identity.'
              : 'Usaremos tu altura y peso únicamente para calcular la talla que mejor se adapta a ti. Estos datos no se vinculan con tu identidad.'
            }</p>

            <button type="button" class="fit-drawer__submit" data-fit-submit disabled>
              ${isEn ? 'CONTINUE' : 'CONTINUAR'}
            </button>

            <div class="fit-accordion" data-fit-accordion style="margin-top: 36px;">
              <button type="button" class="fit-accordion-trigger">
                <span>${isEn ? 'SIZE GUIDE' : 'GUÍA DE TALLAS'}</span>
                <span class="fit-accordion-chevron">˅</span>
              </button>
              <div class="fit-accordion-content">${tableHtml}</div>
            </div>
          </div>
        </div>

        <div class="fit-drawer__result-view" data-fit-result-view style="display:none;">
          <div data-fit-result-container></div>

          <div class="fit-accordion" data-fit-accordion-result style="margin-top: 32px;">
            <button type="button" class="fit-accordion-trigger">
              <span>${isEn ? 'SIZE GUIDE' : 'GUÍA DE TALLAS'}</span>
              <span class="fit-accordion-chevron">˅</span>
            </button>
            <div class="fit-accordion-content">${tableHtml}</div>
          </div>
        </div>
      </div>
    `;

    document.body.appendChild(this._drawer);

    // Populate options
    this._populateSelectOptions('height');
    this._populateSelectOptions('weight');

    // Wire close events
    this._overlay.addEventListener('click', () => this.close());
    this._drawer.querySelector('[data-fit-close]').addEventListener('click', () => this.close());

    // Wire submit
    this._drawer.querySelector('[data-fit-submit]').addEventListener('click', () => {
      if (this._onSubmitCallback) this._onSubmitCallback();
    });

    // Wire Accordions
    const wireAccordion = (accordionEl) => {
      const trigger = accordionEl.querySelector('.fit-accordion-trigger');
      const content = accordionEl.querySelector('.fit-accordion-content');
      if (!trigger || !content) return;

      trigger.addEventListener('click', () => {
        const isOpen = accordionEl.classList.contains('is-open');
        if (isOpen) {
          accordionEl.classList.remove('is-open');
          content.style.display = 'none';
        } else {
          accordionEl.classList.add('is-open');
          content.style.display = 'block';
        }
      });
    };
    this._drawer.querySelectorAll('.fit-accordion').forEach(wireAccordion);

    // Wire height toggles
    this._drawer.querySelectorAll('[data-fit-height-toggle] .fit-unit-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const targetUnit = btn.dataset.unit;
        if (this._heightUnit === targetUnit) return;

        this._heightUnit = targetUnit;
        this._updateUnitToggleUI('height', targetUnit);
        this._populateSelectOptions('height');
      });
    });

    // Wire weight toggles
    this._drawer.querySelectorAll('[data-fit-weight-toggle] .fit-unit-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const targetUnit = btn.dataset.unit;
        if (this._weightUnit === targetUnit) return;

        this._weightUnit = targetUnit;
        this._updateUnitToggleUI('weight', targetUnit);
        this._populateSelectOptions('weight');
      });
    });

    // Enable submit button when both fields have selected values
    const heightInput = this._drawer.querySelector('[data-fit-height]');
    const weightInput = this._drawer.querySelector('[data-fit-weight]');
    const submitBtn = this._drawer.querySelector('[data-fit-submit]');
    const enableSubmit = () => {
      const hVal = heightInput.value;
      const wVal = weightInput.value;
      submitBtn.disabled = !(hVal !== '' && wVal !== '');
      this._updateSelectColor(heightInput);
      this._updateSelectColor(weightInput);
    };
    heightInput.addEventListener('change', enableSubmit);
    weightInput.addEventListener('change', enableSubmit);
  }

  _populateSelectOptions(type) {
    const select = this._drawer ? this._drawer.querySelector(`[data-fit-${type}]`) : null;
    if (!select) return;

    const currentValue = select.value;
    select.innerHTML = '';

    const placeholderOpt = document.createElement('option');
    placeholderOpt.value = '';
    placeholderOpt.textContent = this._lang === 'en' ? 'SELECT' : 'SELECCIONA';
    placeholderOpt.disabled = true;
    placeholderOpt.selected = !currentValue;
    select.appendChild(placeholderOpt);

    if (type === 'height') {
      const isM = this._heightUnit === 'm';
      for (let h = 150; h <= 205; h++) {
        const opt = document.createElement('option');
        opt.value = h;
        if (isM) {
          opt.textContent = `${(h / 100).toFixed(2)} M`;
        } else {
          opt.textContent = `${h} CM`;
        }
        if (currentValue && parseInt(currentValue) === h) {
          opt.selected = true;
        }
        select.appendChild(opt);
      }
    } else {
      const isLbs = this._weightUnit === 'lbs';
      for (let w = 45; w <= 120; w++) {
        const opt = document.createElement('option');
        opt.value = w;
        if (isLbs) {
          opt.textContent = `${Math.round(w / 0.45359237)} LBS`;
        } else {
          opt.textContent = `${w} KG`;
        }
        if (currentValue && parseInt(currentValue) === w) {
          opt.selected = true;
        }
        select.appendChild(opt);
      }
    }
    this._updateSelectColor(select);
  }

  _updateSelectColor(select) {
    if (!select) return;
    if (select.value === '') {
      select.classList.add('has-no-value');
    } else {
      select.classList.remove('has-no-value');
    }
  }

  _updateUnitToggleUI(type, unit) {
    const selector = `[data-fit-${type}-toggle]`;
    const container = this._drawer ? this._drawer.querySelector(selector) : null;
    if (container) {
      container.querySelectorAll('.fit-unit-btn').forEach(btn => {
        btn.classList.toggle('is-active', btn.dataset.unit === unit);
      });
    }
  }

  open(profile, productData, triggerEl) {
    if (!this._drawer) return;

    this._triggerElement = triggerEl || document.activeElement;
    this._productData = productData;
    this._productType = getProductType(productData ? productData.id : null) || 't01';

    // Reset units to CM/KG
    this._heightUnit = 'cm';
    this._weightUnit = 'kg';
    this._updateUnitToggleUI('height', 'cm');
    this._updateUnitToggleUI('weight', 'kg');

    // Populate clean select options
    this._populateSelectOptions('height');
    this._populateSelectOptions('weight');

    const hInput = this._drawer.querySelector('[data-fit-height]');
    const wInput = this._drawer.querySelector('[data-fit-weight]');

    let hasValidProfile = false;

    if (profile && typeof profile.height === 'number' && profile.height > 0 && typeof profile.weight === 'number' && profile.weight > 0) {
      if (hInput) hInput.value = Math.round(profile.height);
      if (wInput) wInput.value = Math.round(profile.weight);

      const engine = new FitRecommendationEngine();
      if (engine.validate(profile.height, profile.weight).valid) {
        hasValidProfile = true;
      }
    } else {
      if (hInput) hInput.value = '';
      if (wInput) wInput.value = '';
    }

    this._updateSelectColor(hInput);
    this._updateSelectColor(wInput);

    this._clearErrors();

    // Enable/disable submit based on prefilled values
    const submitBtn = this._drawer.querySelector('[data-fit-submit]');
    if (submitBtn) {
      const hVal = (hInput ? hInput.value : '').trim();
      const wVal = (wInput ? wInput.value : '').trim();
      submitBtn.disabled = !(hVal !== '' && wVal !== '');
    }

    this._drawer.setAttribute('aria-hidden', 'false');
    this._overlay.setAttribute('aria-hidden', 'false');

    // Trigger reflow, then animate
    requestAnimationFrame(() => {
      this._drawer.classList.add('is-open');
      this._overlay.classList.add('is-visible');
      document.body.classList.add('fit-open');
      document.documentElement.classList.add('fit-open');
    });

    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', this._boundKeydown);
    document.addEventListener('keydown', this._focusTrapBound);

    if (hasValidProfile && profile) {
      const engine = new FitRecommendationEngine();
      const result = engine.recommend(profile.height, profile.weight, this._productType);
      const resolver = new ShopifyVariantResolver(productData || window._fitCurrentProduct || { sizes: ['S','M','L','XL'] });
      const available = resolver.isAvailable(result.size);
      const productSizesNormalized = productData && productData.sizes 
        ? productData.sizes.map(s => String(s).trim().toUpperCase()) 
        : [];
      const sizeExists = productSizesNormalized.includes(String(result.size).trim().toUpperCase());

      this.showResult(result.size, available, this._lang, sizeExists);
    } else {
      this._showFormView();
      // Focus first select dropdown
      setTimeout(() => {
        if (hInput) hInput.focus();
      }, 350);
    }

    this._isOpen = true;
  }

  close() {
    if (!this._drawer) return;

    this._drawer.classList.remove('is-open');
    this._overlay.classList.remove('is-visible');
    document.body.classList.remove('fit-open');
    document.documentElement.classList.remove('fit-open');

    if (this._onCloseCallback) {
      this._onCloseCallback();
    }

    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('ma:fit-updated', { detail: { action: 'close' } }));
    }

    // Unlock scroll after transition
    setTimeout(() => {
      this._drawer.setAttribute('aria-hidden', 'true');
      this._overlay.setAttribute('aria-hidden', 'true');
      const sizeGuideOpen = document.getElementById('size-guide-modal')?.classList.contains('is-open');
      if (!sizeGuideOpen) {
        document.body.style.overflow = '';
      }
    }, 380);

    document.removeEventListener('keydown', this._boundKeydown);
    document.removeEventListener('keydown', this._focusTrapBound);

    const sizeGuideOpen = document.getElementById('size-guide-modal')?.classList.contains('is-open');
    if (!sizeGuideOpen && this._triggerElement && typeof this._triggerElement.focus === 'function') {
      setTimeout(() => this._triggerElement.focus(), 400);
    }

    this._isOpen = false;
  }

  showResult(size, available, lang, sizeExists) {
    const resultView = this._drawer.querySelector('[data-fit-result-view]');
    const formView = this._drawer.querySelector('[data-fit-form-view]');
    const container = this._drawer.querySelector('[data-fit-result-container]');
    const isEn = lang === 'en';
    const isProductPage = window.location.pathname.includes('/products/');

    if (!resultView || !container) return;

    // Show result view, hide form view
    formView.style.display = 'none';
    resultView.style.display = '';

    const productId = this._productData ? this._productData.id : '';
    const productImgUrl = getProductImageUrl(productId, this._productData);

    const hSelect = this._drawer.querySelector('[data-fit-height]');
    const wSelect = this._drawer.querySelector('[data-fit-weight]');
    const hOpt = hSelect ? hSelect.options[hSelect.selectedIndex] : null;
    const wOpt = wSelect ? wSelect.options[wSelect.selectedIndex] : null;
    const heightFormatted = hOpt ? hOpt.textContent : '--';
    const weightFormatted = wOpt ? wOpt.textContent : '--';

    container.innerHTML = `
      <h2 class="fit-drawer__title" style="margin-bottom: 20px;">${isEn ? 'YOUR SIZE' : 'TU TALLA'}</h2>
      
      ${productImgUrl ? `
      <div class="fit-drawer__product-image-container" style="text-align: center; margin-bottom: 20px; background: transparent; padding: 0;">
        <img src="${productImgUrl}" alt="" class="fit-drawer__product-image" style="max-height: 240px; object-fit: contain; width: auto; margin: 0 auto; display: block;">
      </div>
      ` : ''}

      <div class="fit-drawer__recommendation-text-wrap" style="text-align: center; margin-bottom: 24px;">
        <p style="font-size: 11px; font-weight: 400; margin: 0 0 6px; letter-spacing: 0.1em; color: var(--color-black); text-transform: uppercase;">
          ${!available ? (isEn ? 'RECOMMENDED SIZE · OUT OF STOCK' : 'TALLA RECOMENDADA · AGOTADO') : (isEn ? 'RECOMMENDED SIZE' : 'TALLA RECOMENDADA')}
        </p>
        <p style="font-size: 32px; font-weight: 500; margin: 0; font-family: var(--font-base); color: var(--color-black); line-height: 1;">
          <span class="fit-drawer__size-display${!available ? ' fit-drawer__size-display--oos' : ''}">${sizeExists ? size : '--'}</span>
        </p>
      </div>

      <div class="fit-drawer__details-section" style="margin-bottom: 24px; text-align: left;">
        <h3 style="font-size: 11px; font-weight: 400; letter-spacing: 0.1em; text-transform: uppercase; border-bottom: 1px solid var(--color-gray-200); padding-bottom: 8px; margin: 0 0 12px;">
          ${isEn ? 'YOUR DATA' : 'TUS DATOS'}
        </h3>
        <div style="display: flex; justify-content: space-between; align-items: flex-start; font-size: 11px; font-weight: 400; line-height: 1.8; color: var(--color-gray-500); text-transform: uppercase; letter-spacing: 0.05em;">
          <div>
            <p style="margin: 0;">${isEn ? 'HEIGHT' : 'ALTURA'}: ${heightFormatted}</p>
            <p style="margin: 0;">${isEn ? 'WEIGHT' : 'PESO'}: ${weightFormatted}</p>
          </div>
          <div style="text-align: right;">
            <p style="margin: 0;">${isEn ? 'FIT: REGULAR FIT' : 'CORTE: REGULAR FIT'}</p>
          </div>
        </div>
      </div>

      <div class="fit-drawer__actions-wrapper" style="display: flex; flex-direction: column; gap: 12px;">
        ${!sizeExists ? `
          <p style="font-size: 13px; font-weight: 300; text-align: center; color: var(--color-black); line-height: 1.5; margin-bottom: 12px;">
            ${isEn ? 'COULD NOT RECOMMEND A SIZE. Please refer to the size chart to find the most suitable option.' : 'NO PODEMOS RECOMENDAR UNA TALLA DISPONIBLE. Consulta la tabla de medidas para encontrar la opción más adecuada.'}
          </p>
        ` : !available ? renderMarkAltitudeStoqShell(isProductPage, isEn) : `
          <button type="button" class="fit-drawer__apply" data-fit-apply data-fit-apply-size="${size}">
            ${isEn ? `ADD SIZE ${size} TO BAG` : `AÑADIR TALLA ${size} AL CARRITO`}
          </button>
        `}

        <button type="button" class="fit-drawer__recalc-btn" data-fit-recalc>
          ${isEn ? 'EDIT DATA' : 'EDITAR DATOS'}
        </button>

        <button type="button" class="fit-drawer__delete-btn" data-fit-delete-profile>
          ${isEn ? 'CLEAR DATA' : 'BORRAR DATOS'}
        </button>
      </div>
    `;

    if (!available) {
      if (isProductPage) {
        if (typeof mountFitStoqInlineForm === 'function') {
          mountFitStoqInlineForm(productId, this._productData ? this._productData.name : '', size);
        }
      } else {
        const homeForm = container.querySelector('#ma-home-fit-stoq-form');
        if (homeForm) {
          const emailInput = homeForm.querySelector('input[type="email"]');
          const errorMsg = container.querySelector('#ma-home-stoq-error-msg');
          if (emailInput) {
            emailInput.value = '';
            emailInput.addEventListener('input', () => {
              if (errorMsg) errorMsg.style.display = 'none';
            });
          }
          if (errorMsg) errorMsg.style.display = 'none';

          homeForm.addEventListener('submit', async (e) => {
            if (e && e.preventDefault) e.preventDefault();
            const submitBtn = homeForm.querySelector('button[type="submit"]');
            const emailVal = emailInput ? emailInput.value.trim() : '';

            if (!emailVal) return;
            if (errorMsg) errorMsg.style.display = 'none';

            if (submitBtn) {
              submitBtn.disabled = true;
              submitBtn.textContent = isEn ? 'SENDING...' : 'ENVIANDO...';
            }

            const prodName = this._productData ? this._productData.name : '';
            if (typeof window.resolveVariantIdForStoq === 'function') {
              const info = await window.resolveVariantIdForStoq(productId, prodName, size);
              if (!info || !info.productId || !info.variantId) {
                console.error('[MA STOQ HOME] Product/variant resolution failed');
                if (submitBtn) {
                  submitBtn.disabled = false;
                  submitBtn.textContent = isEn ? 'NOTIFY ME' : 'AVÍSAME';
                }
                if (errorMsg) errorMsg.style.display = 'block';
                return;
              }

              const shopDomain = window._RestockRocketConfig?.shop || window.Shopify?.shop;
              if (!shopDomain) {
                console.error('[MA STOQ HOME INTENT ERROR]', { status: 'missing_shop_domain', errors: 'shopDomain is missing' });
                if (submitBtn) {
                  submitBtn.disabled = false;
                  submitBtn.textContent = isEn ? 'NOTIFY ME' : 'AVÍSAME';
                }
                if (errorMsg) errorMsg.style.display = 'block';
                return;
              }

              const locale = document.documentElement.lang?.split('-')[0] || 'es';
              const payload = {
                intent: {
                  shopify_variant_id: Number(info.variantId),
                  shopify_product_id: Number(info.productId),
                  channel: 'email',
                  quantity: 1,
                  source: 'api'
                },
                customer: {
                  email: emailVal,
                  locale: locale
                }
              };

              const marketId =
                window._RestockRocketConfig?.market_id ??
                window._RestockRocketConfig?.marketId ??
                window.Shopify?.marketId;

              const numericMarketId = Number(marketId);

              if (Number.isFinite(numericMarketId) && numericMarketId > 0) {
                payload.intent.shopify_market_id = numericMarketId;
                payload.customer.shopify_market_id = numericMarketId;
              }

              try {
                const response = await fetch('https://app.stoqapp.com/api/v1/intents.json', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'X-Shopify-Shop-Domain': shopDomain
                  },
                  body: JSON.stringify(payload)
                });

                const data = await response.json();

                const hasNoStoqErrors = (errors) => {
                  if (errors == null) return true;
                  if (Array.isArray(errors)) return errors.length === 0;
                  if (typeof errors === 'object') return Object.keys(errors).length === 0;
                  if (typeof errors === 'string') return errors.trim() === '';
                  return false;
                };

                const isSuccess =
                  response.status === 201 &&
                  Array.isArray(data.intents) &&
                  data.intents.length > 0 &&
                  hasNoStoqErrors(data.errors);

                const normalizedSubmittedEmail = emailVal.trim().toLowerCase();

                const matchedIntent = isSuccess
                  ? data.intents.find(item =>
                      Number(item.shopify_product_id) === Number(info.productId) &&
                      Number(item.shopify_variant_id) === Number(info.variantId) &&
                      String(item.email || '').trim().toLowerCase() === normalizedSubmittedEmail
                    )
                  : null;

                if (isSuccess && matchedIntent) {
                  const formContainer = container.querySelector('#ma-stoq-form-container');
                  const successMsg = container.querySelector('#ma-fit-stoq-success-msg');
                  if (formContainer) formContainer.style.display = 'none';
                  if (successMsg) {
                    successMsg.hidden = false;
                    successMsg.style.display = 'flex';
                  }
                } else {
                  throw { status: response.status, errors: data.errors || data.error || 'Invalid intent response' };
                }
              } catch (err) {
                console.error('[MA STOQ HOME INTENT ERROR]', {
                  status: err?.status || 'network_error',
                  errors: err?.errors || err?.message || err
                });

                if (submitBtn) {
                  submitBtn.disabled = false;
                  submitBtn.textContent = isEn ? 'NOTIFY ME' : 'AVÍSAME';
                }

                if (errorMsg) errorMsg.style.display = 'block';
              }
            }
          });
        }
      }
    } else {
      if (window._RestockRocket && typeof window._RestockRocket.removeInlineForm === 'function') {
        try { window._RestockRocket.removeInlineForm(); } catch (_) {}
      }
    }

    const applyBtn = container.querySelector('[data-fit-apply]');
    if (applyBtn) {
      applyBtn.dataset.fitApplySize = size;
      applyBtn.addEventListener('click', () => {
        if (this._onApplyCallback) this._onApplyCallback();
      });
    }

    // Wire edit/recalculate button
    const recalcBtn = container.querySelector('[data-fit-recalc]');
    if (recalcBtn) {
      recalcBtn.addEventListener('click', () => {
        this._showFormView();
        if (this._onRecalcCallback) this._onRecalcCallback();
      });
    }

    // Wire delete button
    const deleteBtn = container.querySelector('[data-fit-delete-profile]');
    if (deleteBtn) {
      deleteBtn.addEventListener('click', () => {
        const repository = new FitProfileRepository();
        repository.clear();
        window.MA_RECOMMENDED_SIZE = null;
        
        const sync = new PDPSizeSynchronizer();
        sync.resetInline(this._lang);
        
        if (typeof window.syncSelectedSizeText === 'function') {
          const activeBtn = document.querySelector('[data-fit-sizes] .size-btn.is-active');
          const activeSize = activeBtn ? activeBtn.getAttribute('data-size') : null;
          window.syncSelectedSizeText(activeSize);
        }
        
        const sizeBtn = document.getElementById('ma-size-btn');
        if (sizeBtn) {
          updateStickyBarNoProfile(this._lang);
        }

        const hSelect = this._drawer.querySelector('[data-fit-height]');
        const wSelect = this._drawer.querySelector('[data-fit-weight]');
        if (hSelect) {
          hSelect.value = '';
          this._updateSelectColor(hSelect);
        }
        if (wSelect) {
          wSelect.value = '';
          this._updateSelectColor(wSelect);
        }

        this._showFormView();
        if (this._onClearCallback) this._onClearCallback();

        if (typeof initCardFitSystems === 'function') {
          initCardFitSystems(this._lang);
        }
      });
    }

    // ── Mobile: Enable CSS :active on iOS Safari ──
    if ('ontouchstart' in window) {
      container.querySelectorAll('.fit-drawer__apply, .fit-drawer__recalc-btn, .fit-drawer__delete-btn').forEach(el => {
        el.addEventListener('touchstart', () => {}, { passive: true });
      });
    }

    resultView.classList.add('is-visible');
  }

  showFieldError(field, message) {
    const errorEl = this._drawer ? this._drawer.querySelector(`[data-fit-${field}-error]`) : null;
    const inputEl = this._drawer ? this._drawer.querySelector(`[data-fit-${field}]`) : null;
    if (errorEl) {
      errorEl.textContent = message;
      errorEl.classList.add('is-visible');
    }
    if (inputEl) inputEl.setAttribute('aria-invalid', 'true');
  }

  clearFieldError(field) {
    const errorEl = this._drawer ? this._drawer.querySelector(`[data-fit-${field}-error]`) : null;
    const inputEl = this._drawer ? this._drawer.querySelector(`[data-fit-${field}]`) : null;
    if (errorEl) {
      errorEl.textContent = '';
      errorEl.classList.remove('is-visible');
    }
    if (inputEl) inputEl.removeAttribute('aria-invalid');
  }

  showError(message) {
    this.showFieldError('height', message);
  }

  hideError() {
    this.clearFieldError('height');
    this.clearFieldError('weight');
  }

  hideResult() {
    const resultView = this._drawer ? this._drawer.querySelector('[data-fit-result-view]') : null;
    if (resultView) {
      resultView.classList.remove('is-visible');
      resultView.style.display = 'none';
    }
  }

  getInputs() {
    if (!this._drawer) return { height: '', weight: '' };
    return {
      height: (this._drawer.querySelector('[data-fit-height]') || {}).value || '',
      weight: (this._drawer.querySelector('[data-fit-weight]') || {}).value || ''
    };
  }

  getMetricValues() {
    const { height, weight } = this.getInputs();
    const hVal = parseInt(height);
    const wVal = parseInt(weight);

    if (isNaN(hVal) || isNaN(wVal)) return null;

    return { heightCm: hVal, weightKg: wVal };
  }

  onSubmit(callback) { this._onSubmitCallback = callback; }
  onApply(callback) { this._onApplyCallback = callback; }
  onRecalc(callback) { this._onRecalcCallback = callback; }
  onClear(callback) { this._onClearCallback = callback; }
  onClose(callback) { this._onCloseCallback = callback; }

  _showFormView() {
    const formView = this._drawer ? this._drawer.querySelector('[data-fit-form-view]') : null;
    const resultView = this._drawer ? this._drawer.querySelector('[data-fit-result-view]') : null;
    if (formView) formView.style.display = '';
    if (resultView) {
      resultView.style.display = 'none';
      resultView.classList.remove('is-visible');
    }
  }

  _clearErrors() {
    this.clearFieldError('height');
    this.clearFieldError('weight');
  }

  _handleKeydown(e) {
    if (e.key === 'Escape') this.close();
  }

  _focusTrap(e) {
    if (e.key !== 'Tab' || !this._drawer) return;
    const focusable = this._drawer.querySelectorAll(
      'button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// 6. PDPSizeSynchronizer — auto-select + inline UI
// ═══════════════════════════════════════════════════════════════════════════════
class PDPSizeSynchronizer {
  constructor(sizesContainerSelector = '[data-fit-sizes]') {
    this._container = document.querySelector(sizesContainerSelector);
  }

  selectSize(size) {
    const pdpSelect = document.getElementById('pdp-size-select');
    if (pdpSelect) {
      const matchingOption = Array.from(pdpSelect.options).find(option =>
        String(option.dataset.size || option.value).toUpperCase() === String(size).toUpperCase()
      );
      if (matchingOption) {
        pdpSelect.value = matchingOption.value;
        pdpSelect.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    if (!this._container) return false;

    const btn = this._container.querySelector(`[data-size="${size}"]`);
    if (!btn || btn.classList.contains('is-disabled')) return false;

    btn.click();

    document.querySelectorAll('.ma-look-sizes').forEach(container => {
      const bBtn = container.querySelector(`.size-btn[data-size="${size}"]`);
      if (bBtn && !bBtn.classList.contains('is-disabled')) {
        container.querySelectorAll('.size-btn').forEach(b => b.classList.remove('is-active'));
        bBtn.classList.add('is-active');

        if (window._maBundleProduct) {
          updateBundleCTA(window._maBundleProduct);
        }
      }
    });

    return true;
  }

  markRecommended(size) {
    this.clearRecommendedBadge();
    if (!this._container) return;
    const btn = this._container.querySelector(`[data-size="${size}"]`);
    if (!btn) return;
    btn.classList.add('is-recommended');
  }

  clearRecommendedBadge() {
    if (!this._container) return;
    this._container.querySelectorAll('.is-recommended').forEach(b => {
      b.classList.remove('is-recommended');
      const hint = b.querySelector('.size-btn__hint');
      if (hint) hint.remove();
    });
  }

  updateInline(size, available, lang) {
    const inlineEl = document.querySelector('[data-fit-inline]');
    const inlineBundleEl = document.querySelector('[data-fit-inline-bundle]');
    const isEn = lang === 'en';

    const repository = new FitProfileRepository();
    const profile = repository.getProfile();
    let sizeToShow = size;

    if (profile && profile.height && profile.weight) {
      const engine = new FitRecommendationEngine();
      const productId = window._fitCurrentProduct ? window._fitCurrentProduct.id : (document.querySelector('[data-product-id]')?.dataset.productId || null);
      const productType = getProductType(productId) || 't01';
      sizeToShow = engine.recommendSize(profile.height, profile.weight, productType);
    }

    const updateLabel = isEn ? 'Update' : 'Actualizar';
    const labelText = isEn ? 'RECOMMENDED:' : 'RECOMENDADO:';

    const htmlContent = `
      <div class="fit-state fit-state--inline-simple">
        <span class="fit-state__label">${labelText} <strong>${sizeToShow}</strong></span>
        <button type="button" class="fit-state__update" data-fit-open>${updateLabel}</button>
      </div>
    `;

    if (inlineEl) {
      inlineEl.innerHTML = htmlContent;
      inlineEl.style.display = 'block';
    }
    if (inlineBundleEl) {
      inlineBundleEl.innerHTML = htmlContent;
      inlineBundleEl.style.display = 'block';
    }
  }

  resetInline(lang) {
    const inlineEl = document.querySelector('[data-fit-inline]');
    const inlineBundleEl = document.querySelector('[data-fit-inline-bundle]');

    if (inlineEl) {
      inlineEl.innerHTML = '';
      inlineEl.style.display = 'none';
    }
    if (inlineBundleEl) {
      inlineBundleEl.innerHTML = '';
      inlineBundleEl.style.display = 'none';
    }
  }

  showRecommendedLabel() {}
  clearRecommendedLabel() {}
}

function updateStickyBarSize(size, isRecommended, lang) {
  const sizeBtn = document.getElementById('ma-size-btn');
  if (!sizeBtn) return;

  const isEn = lang === 'en';
  if (isRecommended) {
    sizeBtn.innerHTML = `
      <span class="ma-size-label">${isEn ? 'Recommended Size' : 'Talla recomendada'}</span>
      <span class="ma-size-value" id="ma-size-value">${size}</span>
    `;
  } else {
    sizeBtn.innerHTML = `
      <span class="ma-size-label">${isEn ? 'Size' : 'Talla'}</span>
      <span class="ma-size-value" id="ma-size-value">${size}</span>
    `;
  }
}

function updateStickyBarNoProfile(lang) {
  const sizeBtn = document.getElementById('ma-size-btn');
  if (!sizeBtn) return;
  const isEn = lang === 'en';
  sizeBtn.innerHTML = `<span class="ma-size-fit-link">${isEn ? 'Find Your Size' : 'Encuentra tu talla'}</span>`;
}

// ═══════════════════════════════════════════════════════════════════════════════
// CARD-BASED FIT SYSTEM (Home page collection grid & related)
// ═══════════════════════════════════════════════════════════════════════════════
function initCardFitSystems(lang) {
  const cards = document.querySelectorAll('[data-fit-inline-card]');
  if (!cards.length) return;

  const repository = new FitProfileRepository();
  const engine = new FitRecommendationEngine();
  const savedProfile = repository.getProfile();

  cards.forEach(cardContainer => {
    const productId = cardContainer.dataset.fitInlineCard;
    const productData = window.PRODUCTS ? window.PRODUCTS[productId] : null;
    if (!productData) return;

    if (productData.sizes && productData.sizes.includes('O/S')) {
      cardContainer.innerHTML = '';
      return;
    }

    const resolver = new ShopifyVariantResolver(productData);
    const isEn = lang === 'en';
    const productType = getProductType(productId) || 't01';

    const wireEvents = (el) => {
      el.querySelectorAll('[data-fit-open]').forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          sessionStorage.setItem('fit-last-triggered-product-id', productId);

          const drawerUI = window._fitDrawerGlobalInstance || new FitDrawerUI();
          if (!window._fitDrawerGlobalInstance) {
            drawerUI.render(lang);
            window._fitDrawerGlobalInstance = drawerUI;

            drawerUI.onApply(() => {
              const applyBtn = document.querySelector('[data-fit-apply]');
              const sizeToApply = applyBtn ? applyBtn.dataset.fitApplySize : null;
              if (!sizeToApply) return;

              const metrics = drawerUI.getMetricValues();
              if (metrics && repo) {
                repository.saveProfile(metrics.heightCm, metrics.weightKg);
              }

              applyBtn.textContent = isEn ? '✓ SELECTED' : '✓ SELECCIONADO';
              applyBtn.classList.add('is-applied');

              setTimeout(() => {
                drawerUI.close();
                setTimeout(() => {
                  applyBtn.classList.remove('is-applied');
                  initCardFitSystems(lang);
                }, 500);
              }, 300);
            });

            drawerUI.onSubmit(() => {
              const metrics = drawerUI.getMetricValues();
              drawerUI.hideError();

              if (!metrics) return;

              const { heightCm, weightKg } = metrics;
              const validation = engine.validate(heightCm, weightKg);

              if (!validation.valid) {
                if (validation.field === 'height') {
                  if (drawerUI._heightUnit === 'in') {
                    drawerUI.showFieldError('height', isEn
                      ? 'Enter a valid height between 59 and 81 inches.'
                      : 'Ingresa una altura válida entre 59 y 81 pulgadas.');
                  } else {
                    drawerUI.showFieldError('height', isEn
                      ? 'Enter a valid height between 150 and 205 cm.'
                      : 'Ingresa una altura válida entre 150 y 205 cm.');
                  }
                } else {
                  if (drawerUI._weightUnit === 'lbs') {
                    drawerUI.showFieldError('weight', isEn
                      ? 'Enter a valid weight between 99 and 265 lbs.'
                      : 'Ingresa un peso válido entre 99 y 265 lbs.');
                  } else {
                    drawerUI.showFieldError('weight', isEn
                      ? 'Enter a valid weight between 45 and 120 kg.'
                      : 'Ingresa un peso válido entre 45 y 120 kg.');
                  }
                }
                return;
              }

              // Save profile immediately on calculation
              repository.saveProfile(heightCm, weightKg);

              const result = engine.recommend(heightCm, weightKg, productType);
              const available = resolver.isAvailable(result.size);
              const productSizesNormalized = productData.sizes 
                ? productData.sizes.map(s => String(s).trim().toUpperCase()) 
                : [];
              const sizeExists = productSizesNormalized.includes(String(result.size).trim().toUpperCase());

              drawerUI.showResult(result.size, available, lang, sizeExists);
            });
          }
          drawerUI.open(repository.getProfile(), productData, btn);
        });
      });
    };

    if (savedProfile && savedProfile.height && savedProfile.weight && engine.validate(savedProfile.height, savedProfile.weight).valid) {
      const result = engine.recommend(savedProfile.height, savedProfile.weight, productType);
      const available = resolver.isAvailable(result.size);
      const statusText = available ? '' : ` <span class="fit-inline-v2__status-oos">(${isEn ? 'Out of stock' : 'Agotada'})</span>`;

      cardContainer.innerHTML = `
        <div class="fit-inline-v2__result-inline" style="margin-top: 0.5rem; margin-bottom: 0.5rem; justify-content: flex-start; width: 100%;">
          <span class="fit-inline-v2__rec-text">
            ${isEn ? 'RECOMMENDED' : 'RECOMENDADO'}: <strong>${result.size}</strong>${statusText}
          </span>
          <button type="button" class="fit-inline-v2__update-inline-link" data-fit-open style="margin-left: 0.25rem;">
            ${isEn ? 'Update' : 'Actualizar'}
          </button>
        </div>
      `;

      const sizeContainer = document.querySelector(`[data-fit-sizes-card="${productId}"]`);
      if (sizeContainer) {
        sizeContainer.querySelectorAll('.size-btn').forEach(b => {
          if (b.dataset.size === result.size) {
            b.classList.add('is-recommended');
          } else {
            b.classList.remove('is-recommended');
          }
        });
      }
    } else {
      cardContainer.innerHTML = `
        <div class="fit-inline-v2" style="margin-top: 0.5rem; margin-bottom: 0.5rem; align-items: flex-start; flex-direction: row; gap: 0.35rem; justify-content: flex-start; width: 100%;">
          <button type="button" class="fit-inline-v2__link-fit" data-fit-open style="font-size: 0.68rem; text-decoration: underline; text-underline-offset: 3px; color: var(--color-gray-600);">
            ${isEn ? 'Find Your Size' : 'Encuentra tu talla'}
          </button>
        </div>
      `;
    }

    wireEvents(cardContainer);
  });
}

// ═══════════════════════════════════════════════════════════════════════════════
// ORCHESTRATOR — initFitSystem
// ═══════════════════════════════════════════════════════════════════════════════
function initFitSystem(productData, lang) {
  if (!productData || !document.querySelector('[data-fit-sizes]')) return;

  if (productData.sizes && productData.sizes.includes('O/S')) {
    return;
  }

  const productType = getProductType(productData.id) || 't01';
  const repository = new FitProfileRepository();
  const engine     = new FitRecommendationEngine();
  const resolver   = new ShopifyVariantResolver(productData);
  const drawer     = new FitDrawerUI();
  const sync       = new PDPSizeSynchronizer();

  drawer.render(lang);
  window._fitDrawerGlobalInstance = drawer;

  const isEn = lang === 'en';

  // ── Wire: Submit (calculate) ──
  drawer.onSubmit(() => {
    const metrics = drawer.getMetricValues();
    drawer.hideError();

    if (!metrics) return;

    const { heightCm, weightKg } = metrics;
    const validation = engine.validate(heightCm, weightKg);
    let hasError = false;

    if (!validation.valid) {
      if (validation.field === 'height') {
        if (drawer._heightUnit === 'in') {
          drawer.showFieldError('height', isEn
            ? 'Enter a valid height between 59 and 81 inches.'
            : 'Ingresa una altura válida entre 59 y 81 pulgadas.');
        } else {
          drawer.showFieldError('height', isEn
            ? 'Enter a valid height between 150 and 205 cm.'
            : 'Ingresa una altura válida entre 150 y 205 cm.');
        }
      } else {
        if (drawer._weightUnit === 'lbs') {
          drawer.showFieldError('weight', isEn
            ? 'Enter a valid weight between 99 and 265 lbs.'
            : 'Ingresa un peso válido entre 99 y 265 lbs.');
        } else {
          drawer.showFieldError('weight', isEn
            ? 'Enter a valid weight between 45 and 120 kg.'
            : 'Ingresa un peso válido entre 45 y 120 kg.');
        }
      }
      hasError = true;
    }

    if (hasError) return;

    // Save profile immediately on calculation
    repository.saveProfile(heightCm, weightKg);

    const result = engine.recommend(heightCm, weightKg, productType);
    const available = resolver.isAvailable(result.size);
    const productSizesNormalized = productData.sizes 
      ? productData.sizes.map(s => String(s).trim().toUpperCase()) 
      : [];
    const sizeExists = productSizesNormalized.includes(String(result.size).trim().toUpperCase());

    if (result.size) {
      window.MA_RECOMMENDED_SIZE = result.size;
      sync.markRecommended(result.size);
      if (typeof window.syncSelectedSizeText === 'function') {
        const activeBtn = document.querySelector('[data-fit-sizes] .size-btn.is-active');
        const activeSize = activeBtn ? activeBtn.getAttribute('data-size') : result.size;
        window.syncSelectedSizeText(activeSize);
      }
    }

    drawer.showResult(result.size, available, lang, sizeExists);
  });

  // ── Wire: Apply size ──
  drawer.onApply(() => {
    const applyBtn = document.querySelector('[data-fit-apply]');
    const sizeToApply = applyBtn ? applyBtn.dataset.fitApplySize : null;
    if (!sizeToApply) return;

    const metrics = drawer.getMetricValues();
    if (metrics) {
      repository.saveProfile(metrics.heightCm, metrics.weightKg);
      
      const result = engine.recommend(metrics.heightCm, metrics.weightKg, productType);
      const available = resolver.isAvailable(result.size);
      
      window.MA_RECOMMENDED_SIZE = result.size;
      sync.selectSize(sizeToApply);
      sync.updateInline(sizeToApply, available, lang);
      sync.markRecommended(result.size);

      if (typeof window.syncSelectedSizeText === 'function') {
        window.syncSelectedSizeText(sizeToApply);
      }
    }

    applyBtn.textContent = isEn ? '✓ SELECTED' : '✓ SELECCIONADO';
    applyBtn.classList.add('is-applied');

    setTimeout(() => {
      drawer.close();
      
      // Auto-trigger PDP add to basket click
      const pdpAddBtn = document.getElementById('pdp-add');
      if (pdpAddBtn) {
        pdpAddBtn.click();
      }

      setTimeout(() => {
        applyBtn.classList.remove('is-applied');
      }, 500);

      wireOpenButtons();
      initCardFitSystems(lang);
    }, 300);
  });

  function wireOpenButtons() {
    document.querySelectorAll('[data-fit-open]').forEach(btn => {
      if (btn.dataset.fitBound) return;
      btn.dataset.fitBound = 'true';
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const isBundle = btn.closest('[data-fit-inline-bundle]') !== null;
        sessionStorage.setItem('fit-last-triggered-product-id', isBundle ? 'bundle' : 'pdp');
        const profile = repository.getProfile();
        drawer.open(profile, productData, btn);
      });
    });
  }

  const handlePdpSizeSelectFit = (size) => {
    if (!size) return;
    const savedProfile = repository.getProfile();
    const recommendedSize = savedProfile ? engine.recommendSize(savedProfile.height, savedProfile.weight, productType) : null;
    const isRecommended = (size === recommendedSize);

    if (isRecommended) {
      sync.markRecommended(size);
    } else {
      sync.clearRecommendedBadge();
    }

    updateStickyBarSize(size, isRecommended, lang);
  };

  const pdpSizeSelectEl = document.getElementById('pdp-size-select');
  if (pdpSizeSelectEl) {
    pdpSizeSelectEl.addEventListener('change', (e) => {
      handlePdpSizeSelectFit(e.target.value);
    });
  }

  document.querySelectorAll('#pdp-sizes .size-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const size = btn.dataset.size;
      handlePdpSizeSelectFit(size);
    });
  });

  const savedProfile = repository.getProfile();
  if (savedProfile && savedProfile.height && savedProfile.weight && engine.validate(savedProfile.height, savedProfile.weight).valid) {
    const result = engine.recommend(savedProfile.height, savedProfile.weight, productType);
    const available = resolver.isAvailable(result.size);

    if (result.size) {
      window.MA_RECOMMENDED_SIZE = result.size;
      sync.updateInline(result.size, available, lang);
      sync.markRecommended(result.size);
      const hasVariantDeepLink = new URLSearchParams(window.location.search).has('variant');
      if (!hasVariantDeepLink) sync.selectSize(result.size);
      updateStickyBarSize(result.size, true, lang);

      if (typeof window.syncSelectedSizeText === 'function') {
        const activeBtn = document.querySelector('[data-fit-sizes] .size-btn.is-active');
        const activeSize = activeBtn ? activeBtn.getAttribute('data-size') : result.size;
        window.syncSelectedSizeText(activeSize);
      }

      setTimeout(() => wireOpenButtons(), 50);
    }
  } else {
    window.MA_RECOMMENDED_SIZE = null;
    sync.resetInline(lang);
    updateStickyBarNoProfile(lang);

    if (typeof window.syncSelectedSizeText === 'function') {
      const activeBtn = document.querySelector('[data-fit-sizes] .size-btn.is-active');
      const activeSize = activeBtn ? activeBtn.getAttribute('data-size') : null;
      if (activeSize) window.syncSelectedSizeText(activeSize);
    }

    setTimeout(() => wireOpenButtons(), 50);
  }
}

if (typeof window !== 'undefined') {
  window.FitProfileStorage = FitProfileStorage;
  window.FitProfileRepository = FitProfileRepository;
  window.FitRecommendationEngine = FitRecommendationEngine;
  window.ShopifyVariantResolver = ShopifyVariantResolver;
  window.FitDrawerUI = FitDrawerUI;
  window.PDPSizeSynchronizer = PDPSizeSynchronizer;
  window.initFitSystem = initFitSystem;
  window.initCardFitSystems = initCardFitSystems;
  window.getProductType = getProductType;

  document.addEventListener('DOMContentLoaded', () => {
    const lang = document.documentElement.getAttribute('data-lang') || 'es';
    
    try {
      const FIT_MIGRATION_KEY = 'mark-altitude-fit-migrated-v3.1';
      if (!localStorage.getItem(FIT_MIGRATION_KEY)) {
        localStorage.removeItem('markAltitudeSizeFinderV2');
        localStorage.removeItem('mark-altitude-fit-profile');
        localStorage.removeItem('mark-altitude-fit-profile-v3');
        sessionStorage.removeItem('markAltitudeSizeFinderV1');
        localStorage.setItem(FIT_MIGRATION_KEY, 'true');
      }
    } catch(e) {}
    
    if (document.querySelector('[data-fit-inline-card]')) {
      initCardFitSystems(lang);
    }
  });

  document.querySelectorAll('[data-lang]').forEach(btn => {
    btn.addEventListener('click', () => {
      const lang = btn.getAttribute('data-lang');
      setTimeout(() => {
        if (document.querySelector('[data-fit-inline-card]')) {
          initCardFitSystems(lang);
        }
      }, 100);
    });
  });

  const footerCountrySelect = document.getElementById('footer-country');
  if (footerCountrySelect) {
    footerCountrySelect.addEventListener('change', e => {
      const lang = e.target.value;
      setTimeout(() => {
        if (document.querySelector('[data-fit-inline-card]')) {
          initCardFitSystems(lang);
        }
      }, 100);
    });
  }
}

if (typeof module !== 'undefined' && module.exports) {
  module.exports = {
    FitProfileStorage,
    FitProfileRepository,
    FitRecommendationEngine,
    ShopifyVariantResolver,
    FitDrawerUI,
    PDPSizeSynchronizer,
    initFitSystem,
    initCardFitSystems,
    getProductType
  };
}
